import { useMemo } from 'react';
import { planCapital, type CapitalPlanInput } from '../lib/capital';
import { applyScenario, evaluate, type ScenarioName } from '../lib/flip';
import { pounds } from '../lib/format';
import { useStore } from '../state/store';
import { useDraft } from '../state/useDraft';
import { NumberField } from './NumberField';

const isInput = (v: unknown): v is CapitalPlanInput =>
  typeof v === 'object' && v !== null && typeof (v as CapitalPlanInput).startingCash === 'number' && typeof (v as CapitalPlanInput).reinvest === 'boolean';

export function CapitalPage() {
  const { data } = useStore();
  const [input, setInput] = useDraft<CapitalPlanInput>(
    'car-flip-planner:capital',
    () => ({ startingCash: data.settings.startingCash, cashPerCar: 1000, profitPerCar: 400, cycleDays: 30, maxCars: 2, badCarEvery: 5, badCarLossPct: 15, reinvest: true, months: 12 }),
    isInput,
  );
  const set = <K extends keyof CapitalPlanInput>(k: K, v: CapitalPlanInput[K]) => setInput({ ...input, [k]: v });
  const plan = useMemo(() => planCapital(input), [input]);

  // Offer the average of the cars already planned as a starting point.
  const fromCars = (name: ScenarioName) => {
    const live = data.cars.filter((c) => c.status !== 'passed');
    if (live.length === 0) return null;
    const rs = live.map((c) => evaluate(applyScenario(c.deal, name, data.settings.scenarios)));
    const avg = (f: (r: (typeof rs)[number]) => number) => rs.reduce((s, r) => s + f(r), 0) / rs.length;
    const days = live.reduce((s, c) => s + c.deal.holdingDays, 0) / live.length;
    return { cashPerCar: Math.round(avg((r) => r.cashInvested)), profitPerCar: Math.round(avg((r) => r.profit)), cycleDays: Math.round(days + 7) };
  };
  const guess = fromCars('expected');

  return (
    <div className="page two-col">
      <div>
        <h1>Growing your cash</h1>
        <p className="lede">See what a pot of cash could become if you keep buying and selling, with a bad car now and then. A cautious guide, not a promise.</p>
        {guess && (
          <p>
            <button type="button" className="btn small" onClick={() => setInput({ ...input, ...guess })}>
              Use the average of my planned cars ({pounds(guess.cashPerCar)} in, {pounds(guess.profitPerCar)} profit, {guess.cycleDays} days)
            </button>
          </p>
        )}
        <div className="grid">
          <NumberField label="Starting cash" prefix="£" value={input.startingCash} onChange={(n) => set('startingCash', n)} />
          <NumberField label="Cash in each car" prefix="£" value={input.cashPerCar} onChange={(n) => set('cashPerCar', n)} hint="Price plus repairs and costs." />
          <NumberField label="Profit on a good car" prefix="£" value={input.profitPerCar} onChange={(n) => set('profitPerCar', n)} />
          <NumberField label="Days per round" value={input.cycleDays} onChange={(n) => set('cycleDays', n)} hint="Finding, fixing and selling." />
          <NumberField label="Most cars at once" value={input.maxCars} onChange={(n) => set('maxCars', n)} hint="Space, time and insurance limit this." />
          <NumberField label="One bad car in every" value={input.badCarEvery} onChange={(n) => set('badCarEvery', n)} hint="0 means none. 5 is a sensible guess." />
          <NumberField label="Loss on a bad car" suffix="%" value={input.badCarLossPct} onChange={(n) => set('badCarLossPct', n)} hint="Of the cash in it." />
          <NumberField label="Months" value={input.months} onChange={(n) => set('months', n)} />
        </div>
        <label className="check">
          <input type="checkbox" checked={input.reinvest} onChange={(e) => set('reinvest', e.target.checked)} />
          <span>Put profit back into buying more cars</span>
        </label>
      </div>

      <aside className="results" aria-label="Projection">
        <div className="headline">
          <div><span className="label">After {input.months} months</span><span className="big">{pounds(plan.finalPot)}</span></div>
          <div><span className="label">Profit</span><span className={`big ${plan.totalProfit < 0 ? 'neg' : 'pos'}`}>{pounds(plan.totalProfit)}</span></div>
          <div><span className="label">Cars sold</span><span className="big">{plan.carsSold}</span></div>
        </div>
        <p className="muted">Average profit per car, allowing for bad ones: <strong>{pounds(plan.expectedProfitPerCar)}</strong></p>
        {plan.rounds.length === 0 ? (
          <p className="warn" role="note">With these numbers you cannot afford a single car. Raise your starting cash or lower the cash per car.</p>
        ) : (
          <table className="scenarios">
            <thead><tr><th scope="col">Round</th><th scope="col">Cars</th><th scope="col">Cash in</th><th scope="col">Profit</th><th scope="col">Pot</th></tr></thead>
            <tbody>
              {plan.rounds.map((r) => (
                <tr key={r.round}><th scope="row">{r.round}</th><td>{r.carsBought}</td><td>{pounds(r.cashIn)}</td><td>{pounds(r.profit)}</td><td>{pounds(r.potAfter)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="muted small">Real life is lumpier: cars take longer to sell, and one dud can wipe out several wins.</p>
      </aside>
    </div>
  );
}
